import React from "react";
import clsx from "clsx";
import "./sticker-panel.styles.scss";

//@ts-ignore
import { ReactComponent as ExpandIcon } from "../../../assets/icons/Expand.svg";
import { StickerCategories } from "./sticker-categories";
import { Sticker } from "../Sticker";
import { Popover, PopoverContent } from "../Popover";
import { useChatBoxListUIController, useChatBoxViewContext } from "../../contexts";
import { useElementPosition } from "../../hooks/use-element-position";

const BlockName = "sticker-panel";
const ClassNames = {
	Block: BlockName,
	Modifiers: {},
	Elements: {
		PopoverContent: `${BlockName}__popover-content`,
		Header: `${BlockName}__header`,
		HeaderTitle: `${BlockName}__header-title`,
		HeaderButton: `${BlockName}__header-button`,
		Content: `${BlockName}__content`,
		Section: `${BlockName}__section`,
		SectionTitle: `${BlockName}__section-title`,
		SectionContent: `${BlockName}__section-content`,
		StickerItem: `${BlockName}__sticker-item`,
	},
} as const;

export interface StickerPanelProps {
	className?: string;
	children?: React.ReactNode;
}

export type StickerPanelComponent = React.FC<StickerPanelProps> & {
	ClassNames: typeof ClassNames;
};

export const StickerPanel = ((props: StickerPanelProps) => {
	const stickerCategories = React.useMemo(() => StickerCategories, []);
	const controller = useChatBoxListUIController();
	const { toUidInfo, fromUidInfo } = useChatBoxViewContext();
	const { position, onMouseDownElement, resetInitialPosition } = useElementPosition({
		initialValue: { x: 0, y: 0 },
	});
	const handleClickStickerItem = (event: React.MouseEvent<HTMLDivElement>, sticker: any) => {
		const stickerDOMRect = event.currentTarget.getBoundingClientRect();
		controller.addNewStickerMessage(
			{
				toUid: toUidInfo.uid,
				fromUid: fromUidInfo.uid,
				content: { ...sticker },
			},
			{
				shouldPlayAnimation: true,
				stickerDOMRectSnapshot: stickerDOMRect,
			},
		);
	};

	const handleOpenChangePopover = (open: boolean) => {
		!open && resetInitialPosition();
	};

	return (
		<Popover onOpenChange={handleOpenChangePopover}>
			{props.children}
			<PopoverContent
				side="top"
				align="start"
				sideOffset={6}
				className={StickerPanel.ClassNames.Elements.PopoverContent}
				style={{ transform: `translate(${position.x}px, ${position.y}px)` }}
			>
				<div className={clsx(StickerPanel.ClassNames.Block, props.className)}>
					<div className={StickerPanel.ClassNames.Elements.Header}>
						<h2 className={StickerPanel.ClassNames.Elements.HeaderTitle}>
							Sticker Panel
						</h2>
						<button
							onMouseDown={(e) => onMouseDownElement(e)}
							className={StickerPanel.ClassNames.Elements.HeaderButton}
						>
							<ExpandIcon className="w-6 h-6" />
						</button>
					</div>
					<div className={StickerPanel.ClassNames.Elements.Content}>
						{stickerCategories.map((category) => {
							return (
								<div
									key={category.id}
									className={StickerPanel.ClassNames.Elements.Section}
								>
									<h3 className={StickerPanel.ClassNames.Elements.SectionTitle}>
										{category.name}
									</h3>
									<div
										className={StickerPanel.ClassNames.Elements.SectionContent}
									>
										{category.stickers.map((sticker) => {
											return (
												<div
													key={`${sticker.cateId}-${sticker.id}`}
													className={
														StickerPanel.ClassNames.Elements.StickerItem
													}
													onClick={(event) =>
														handleClickStickerItem(event, sticker)
													}
												>
													<Sticker
														size={70}
														autoPlay={false}
														loop={false}
														iterations={5}
														data={sticker}
													/>
												</div>
											);
										})}
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
}) as StickerPanelComponent;
StickerPanel.ClassNames = ClassNames;
